export function normalizeErrorMessage(data: unknown): string {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const d = data as Record<string, unknown>;
    const msg = d.Message ?? d.message;
    if (msg != null) return String(msg);
  }
  return "";
}

type TenantQuery = Record<string, string | string[] | null | undefined>;

/**
 * Resolves the {hotelId}/{outletId} tenant tokens in an API URL from a parsed
 * query object (e.g. the result of query-string's parse of location.search).
 *
 * Tenancy in this app lives entirely in the URL path, and the axios request
 * interceptor normally substitutes these tokens from location.search at DISPATCH
 * time. That is wrong for offline writes: the pending queue is replayed after
 * reconnect against whatever page the user happens to be on then, so a queued
 * write can silently land in the wrong hotel/outlet (see GH-1322 / PMS-514).
 * Callers freeze the tenant context by resolving the URL at ENQUEUE time with
 * this helper and persisting the resolved (token-free) URL.
 *
 * - Repeated query params (string[]) collapse to their first value.
 * - Returns null when the URL still needs a token whose param is absent/empty, so
 *   callers can refuse to persist an "/hotel/undefined/..." request rather than
 *   baking the literal string "undefined" into the queue.
 * - Idempotent: a URL that carries no tokens is returned unchanged, so replaying
 *   an already-resolved URL back through the interceptor is a safe no-op.
 */
export function resolveTenantUrl(url: string | undefined, query: TenantQuery = {}): string | null {
  if (!url) return null;

  const pick = (value: string | string[] | null | undefined): string | undefined => {
    const raw = Array.isArray(value) ? value[0] : value;
    return raw == null || raw === "" || raw === "undefined" ? undefined : raw;
  };
  const hotelId = pick(query.hotelId);
  const outletId = pick(query.outletId);

  if (url.includes("{hotelId}") && hotelId === undefined) return null;
  if (url.includes("{outletId}") && outletId === undefined) return null;

  return url
    .replaceAll("{hotelId}", String(hotelId))
    .replaceAll("{outletId}", String(outletId));
}
